import { getPxAnonymousId, PX_ANONYMOUS_ID_KEY } from './anonymous-id';
import { dataLayerAdapter } from './data-layer-adapter';
import { gaAdapter } from './ga-adapter';
import { posthogAdapter } from './posthog-adapter';
import { rudderStackAdapter } from './rudderstack-adapter';

/**
 * @typedef {Object} AnalyticsDestination
 * @property {(event: string, properties: Object, traits?: Object) => void} [track]
 * @property {(traits: Object) => void} [identify]
 *
 * @typedef {Object} AnalyticsIdentityProvider
 * @property {() => Promise<string|null>} whenAnonymousId
 *
 * @typedef {Object} Analytics
 * @property {(event: string, properties?: Object) => void} track
 * @property {(traits: Object) => void} identify
 * @property {() => Promise<string|null>} whenAnonymousId
 */

function callDestinations(destinations, operation, ...args) {
  destinations.forEach((destination) => {
    if (typeof destination[operation] !== 'function') return;

    try {
      destination[operation](...args);
    } catch (error) {
      // Analytics delivery is best-effort; one destination must not block the others.
    }
  });
}

/**
 * @param {{ destinations: AnalyticsDestination[], identityProvider?: AnalyticsIdentityProvider }} ports
 * @returns {Analytics}
 */
export function createAnalytics({ destinations, identityProvider }) {
  const userTraits = {};

  return {
    identify(traits = {}) {
      if (typeof window === 'undefined') return;

      Object.assign(userTraits, traits);
      destinations.forEach((destination) => {
        callDestinations([destination], 'identify', { ...userTraits });
      });
    },

    track(event, properties = {}) {
      if (typeof window === 'undefined') return;

      let pxAnonymousId = null;
      try {
        // Reader only — cookie is created on route update (`getOrCreatePxAnonymousId`).
        pxAnonymousId = getPxAnonymousId();
      } catch {
        // Cookie access can throw (e.g. opaque origin); still deliver without the join key.
      }
      const payload = {
        ...userTraits,
        ...properties,
        ...(pxAnonymousId ? { [PX_ANONYMOUS_ID_KEY]: pxAnonymousId } : {})
      };
      destinations.forEach((destination) => {
        callDestinations([destination], 'track', event, { ...payload });
      });
    },

    whenAnonymousId() {
      return typeof window === 'undefined' || !identityProvider
        ? Promise.resolve(null)
        : identityProvider.whenAnonymousId();
    }
  };
}

/** Legacy path: UTM, experiments — RudderStack / dataLayer / PostHog. Unchanged for prod. */
export const analytics = createAnalytics({
  destinations: [rudderStackAdapter, dataLayerAdapter, posthogAdapter],
  identityProvider: rudderStackAdapter
});

/**
 * Marketing M: catalog only — direct GA4 (gtag) plus PostHog when the snippet loads.
 * Does not fan out to RudderStack/Mixpanel or the GTM dataLayer adapter.
 */
export const marketingAnalytics = createAnalytics({
  destinations: [gaAdapter, posthogAdapter]
});
